// Zustand store for the pipeline graph + per-node runtime state.
// React Flow is driven in controlled mode from this store.

import { create } from 'zustand';
import {
  type Node,
  type Edge,
  type Connection,
  type NodeChange,
  type EdgeChange,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
} from '@xyflow/react';
import { NODE_SPECS } from './registry';
import { NodeRuntime } from './types';

let idCounter = 1;
const nextId = () => `n${idCounter++}`;

interface CacheEntry {
  key: string;
  output: Record<string, unknown>;
}

interface PipelineState {
  nodes: Node[];
  edges: Edge[];
  runtime: Record<string, NodeRuntime>;
  cache: Record<string, CacheEntry>;
  running: boolean;

  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (conn: Connection) => void;

  addNode: (type: string, position?: { x: number; y: number }) => void;
  updateNodeData: (id: string, data: Record<string, unknown>) => void;
  removeNode: (id: string) => void;
  pruneNodeState: (ids: string[]) => void;

  setRuntime: (id: string, rt: Partial<NodeRuntime>) => void;
  resetRuntime: () => void;
  setCacheEntry: (id: string, entry: CacheEntry) => void;
  clearCache: () => void;
  setRunning: (v: boolean) => void;
  clearGraph: () => void;
  loadGraph: (nodes: Node[], edges: Edge[]) => void;
}

export const usePipelineStore = create<PipelineState>((set, get) => ({
  nodes: [],
  edges: [],
  runtime: {},
  cache: {},
  running: false,

  onNodesChange: (changes) => set({ nodes: applyNodeChanges(changes, get().nodes) }),
  onEdgesChange: (changes) => set({ edges: applyEdgeChanges(changes, get().edges) }),
  // One incoming edge per target handle: drop any existing edge into the same handle first.
  onConnect: (conn) =>
    set({
      edges: addEdge(
        conn,
        get().edges.filter(
          (e) => !(e.target === conn.target && e.targetHandle === conn.targetHandle),
        ),
      ),
    }),

  addNode: (type, position) => {
    const spec = NODE_SPECS[type];
    if (!spec) return;
    const count = get().nodes.length;
    // Lay click-added nodes out on a grid so they don't stack (nodes are ~240px wide).
    const pos = position ?? { x: 80 + (count % 3) * 300, y: 60 + Math.floor(count / 3) * 240 };
    const node: Node = { id: nextId(), type, position: pos, data: { ...spec.defaultData } };
    set({ nodes: [...get().nodes, node] });
  },

  updateNodeData: (id, data) =>
    set({
      nodes: get().nodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, ...data } } : n,
      ),
    }),

  removeNode: (id) => {
    const { [id]: _rt, ...runtime } = get().runtime;
    const { [id]: _c, ...cache } = get().cache;
    set({
      nodes: get().nodes.filter((n) => n.id !== id),
      edges: get().edges.filter((e) => e.source !== id && e.target !== id),
      runtime,
      cache,
    });
  },

  pruneNodeState: (ids) => {
    const runtime = { ...get().runtime };
    const cache = { ...get().cache };
    for (const id of ids) {
      delete runtime[id];
      delete cache[id];
    }
    set({ runtime, cache });
  },

  setRuntime: (id, rt) =>
    set({
      runtime: {
        ...get().runtime,
        [id]: { ...(get().runtime[id] ?? { status: 'idle' }), ...rt },
      },
    }),
  resetRuntime: () => set({ runtime: {} }),
  setCacheEntry: (id, entry) => set({ cache: { ...get().cache, [id]: entry } }),
  clearCache: () => set({ cache: {} }),
  setRunning: (v) => set({ running: v }),
  clearGraph: () => set({ nodes: [], edges: [], runtime: {}, cache: {} }),

  loadGraph: (nodes, edges) => {
    // Bump the id counter past any loaded id so new nodes don't collide.
    let maxId = 0;
    for (const n of nodes) {
      const m = /^n(\d+)$/.exec(n.id);
      if (m) maxId = Math.max(maxId, parseInt(m[1], 10));
    }
    idCounter = maxId + 1;
    set({ nodes, edges, runtime: {}, cache: {} });
  },
}));
